import { blobEndpoint, datasetsContainer } from '~lib/storage/config';
import type { Dataset, DatasetFile } from '~lib/storage/datasets';

const ABS_API_VERSION = '2024-08-04';

// Concurrent file fetches. Higher numbers help when files are small + the
// upstream is fast; lower numbers protect a flaky link from N parallel resets.
// 4 is the same default as the upload path.
const MAX_FILE_CONCURRENCY = 4;

export interface DownloadDatasetOptions {
  signal?: AbortSignal;
  onProgress?: (bytesDownloaded: number) => void;
}

// Feature-detect File System Access API. Chrome/Edge/Opera ship it; Firefox
// and Safari do not. Without it we can't stream files to a folder of the
// user's choosing — see datasetDownloadSupported() callers for the UX.
export function datasetDownloadSupported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

export async function downloadDatasetToFolder(
  token: string,
  dataset: Dataset,
  options: DownloadDatasetOptions = {},
): Promise<void> {
  const showDirectoryPicker = window.showDirectoryPicker;
  if (!showDirectoryPicker) {
    throw new Error(
      'This browser does not support saving to a folder. Use Chrome, Edge, or Opera, or download files individually from the expanded view.',
    );
  }

  // The picker prompt is a user-gesture-gated browser API; the caller must
  // invoke this from inside an onClick (no setTimeout, no microtask delay).
  const root = await showDirectoryPicker({
    id: 'senscience-dataset-catalog',
    mode: 'readwrite',
    startIn: 'downloads',
  });

  // Make a top-level folder named after the dataset so the user can pick e.g.
  // their Downloads root and end up with `~/Downloads/<dataset-name>/...`.
  const datasetRoot = await root.getDirectoryHandle(dataset.name, {
    create: true,
  });

  let bytesDownloaded = 0;
  const queue = dataset.files.slice();

  const workers = Array.from(
    { length: Math.min(MAX_FILE_CONCURRENCY, queue.length) },
    async () => {
      for (;;) {
        const file = queue.shift();
        if (!file) return;
        await downloadOneFile(token, datasetRoot, file, options.signal);
        bytesDownloaded += file.size;
        options.onProgress?.(bytesDownloaded);
      }
    },
  );

  await Promise.all(workers);
}

async function downloadOneFile(
  token: string,
  datasetRoot: FileSystemDirectoryHandle,
  file: DatasetFile,
  signal: AbortSignal | undefined,
): Promise<void> {
  const segments = file.path.split('/');
  const fileName = segments.pop();
  if (!fileName) {
    throw new Error(`Invalid blob path: ${file.path}`);
  }

  let dir = datasetRoot;
  for (const segment of segments) {
    dir = await dir.getDirectoryHandle(segment, { create: true });
  }

  const url = `${blobEndpoint}/${datasetsContainer}/${encodeBlobPath(file.fullPath)}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-ms-version': ABS_API_VERSION,
    },
    signal,
  });

  if (!response.ok || !response.body) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `Download failed for ${file.path}: ${response.status} ${response.statusText}${
        body ? ` — ${body.slice(0, 200)}` : ''
      }`,
    );
  }

  const fileHandle = await dir.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  // pipeTo streams the response straight to disk — never materialises the
  // full blob in memory. The browser handles backpressure between the
  // network and the file system.
  await response.body.pipeTo(writable, { signal });
}

function encodeBlobPath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}
